import 'bootstrap'

import $ from 'jquery'
import get from 'lodash/get'
import isNil from 'lodash/isNil'
import set from 'lodash/set'
import OLCollection from 'ol/Collection'
import { Attribution, defaults as defaultControls } from 'ol/control'
import OLFeature from 'ol/Feature'
import OLGeomLineString from 'ol/geom/LineString'
import OLGeomMultiPoint from 'ol/geom/MultiPoint'
import OLGeomPoint from 'ol/geom/Point'
import OLLayerVector from 'ol/layer/Vector'
import OLMap from 'ol/Map'
import { transform as OLProjTransform } from 'ol/proj'
import OLSourceVector from 'ol/source/Vector'
import OLStyleCircle from 'ol/style/Circle'
import OLStyleFill from 'ol/style/Fill'
import OLStyleStroke from 'ol/style/Stroke'
import OLStyleStyle from 'ol/style/Style'
import OLView from 'ol/View'
import { reduxStore } from 'reduxStore'

import { setDirectionsTravelDistanceKM } from 'api/actions'
import PoziAPIData from 'api/data'
import { HistoryUpdate } from 'api/history'
import * as Model from 'api/model'
import * as PoziUtils from 'api/utils'
import { setLayerSelected, setLayerUnselected } from 'drawer/tabs/layers/actions'
import { setMapLoaded } from 'map/actions'
import AddScaleLine from 'map/AddScaleLine'
import GetMapExtents from 'map/GetMapExtents'
import PoziMap from 'map/map'
import { disableTooltips, enableTooltips } from 'map/tooltips'
import { activateLayersVisibleOnLoad, setDragZoomUnconstrained } from 'utils/MapUtils'
import PoziNotifications from 'utils/notifications'
import { equalsIgnoreCase } from 'utils/text'

/**
 * Initialisation of the map and related components and behaviour such as geocoding, select marker, feature hightlight.
 */
export default function PoziMapBase () {
  const app = PoziUtils.getApp()

  // ============================================================
  // Public Variables
  // ============================================================

  /**
   * This object
   * @type {object}
   */
  const __ = this

  // Source the config from the app variable
  __.config = {}

  let clickEventsKey
  __.clickEventsKey = clickEventsKey

  // ============================================================
  // Private Variables
  // ============================================================

  let highlightFeature

  // ============================================================
  // Public Functions
  // ============================================================

  __.unhighlightAdditionalOverlayFeature = function () {
    if (app.featureOverlay) {
      if (highlightFeature) {
        try {
          app.featureOverlay.getSource().removeFeature(highlightFeature)
        } catch (e) {
          console.log(e)
        }
        highlightFeature = null
      }
    }
  }

  __.highlightAdditionalOverlayFeature = function (olFeature) {
    if (app.featureOverlay) {
      if (olFeature) {
        highlightFeature = olFeature
        app.featureOverlay.getSource().addFeature(olFeature)
      }
    }
  }

  __.highlightOnlyOneOverlayFeature = function (olFeature) {
    // http://openlayers.org/en/latest/apidoc/ol.source.Vector.html#clear
    if (app.featureOverlay) {
      app.featureOverlay.getSource().clear()
      if (olFeature) {
        __.highlightAdditionalOverlayFeature(olFeature)
        highlightFeature = olFeature
      } else {
        highlightFeature = null
      }
    }
  }

  __.removeOverlayFeature = function (olFeature) {
    if (app.featureOverlay) {
      app.featureOverlay.getSource().removeFeature(olFeature)
    }
  }

  __.removeAllOverlayFeatures = function () {
    if (app.featureOverlay) {
      console.log('removeAllOverlayFeatures...')
      app.featureOverlay.getSource().clear()
      highlightFeature = null
    }
  }

  __.initMap = function () {
    return new Promise(function (resolve, reject) {
      __.config = app.siteConfig

      // Viewport
      const minZoom = Model.get('config.layout.map.views.minZoom', 1)
      const maxZoom = Model.get('config.layout.map.views.maxZoom', 20)
      const viewOpts = {
        minZoom, // Global min
        maxZoom, // Global max
        enableRotation: false
      }

      // Configuration
      const bbox = Model.get('config.layout.map.views.bbox', [])
      if (bbox.length === 0) {
        bbox.push([108, -8])
        bbox.push([158, -44])
        Model.set('config.layout.map.views.bbox', bbox)
      }
      set(viewOpts, 'bbox', bbox)

      // The 'extent' option limits the map zoom/pan to this extent
      // (i.e. treats it as a maxExtent).
      set(viewOpts, 'extent', GetMapExtents(bbox))
      set(viewOpts, 'constrainOnlyCenter', true) // Allows for zooming out
      set(viewOpts, 'showFullExtent', true) // Allows for a bit extra padding around initial extent

      // Zoom settings
      set(viewOpts, 'constrainResolution', true) // always zoom the nearest zoom level (integer)
      set(viewOpts, 'smoothResolutionConstraint', true) // allow the view to exceed slightly

      // Override by URL / state
      // http://openlayers.org/en/v3.0.0/apidoc/ol.View.html
      const x = Model.getState('x', null)
      const y = Model.getState('y', null)
      const z = Model.getState('z', null)

      if (x && y) {
        const center = new OLGeomPoint([parseFloat(x), parseFloat(y)]).transform('EPSG:4326', 'EPSG:3857').getCoordinates()
        viewOpts.center = center
      }
      if (z) {
        viewOpts.zoom = z
      }

      let view
      if (!viewOpts.center || !viewOpts.zoom) {
        // Setting the view to a bbox or a diagaonal across Australia
        // Australia will fit in regardless of screen resolution
        const diag = viewOpts.bbox || [[112.5, -43.5], [153.5, -9.5]]
        const coordToFit = diag
        const fitOptions = {}
        fitOptions.size = [$('#map').width() * 1.2, $('#map').height() * 1.2]
        view = new OLView(viewOpts)
        view.fit(
          new OLGeomLineString(
            $.map(coordToFit, function (pt) {
              return [OLProjTransform(pt, 'EPSG:4326', 'EPSG:3857')]
            })
          ),
          fitOptions
        )
      } else {
        // Using configured or URL params
        view = new OLView(viewOpts)
      }

      // Map instantiation
      const mapOptions = {}
      mapOptions.renderer = 'canvas'
      mapOptions.target = 'map'
      mapOptions.view = view

      const attribution = new Attribution({
        collapsible: false,
        collapsed: false
      })
      mapOptions.controls = defaultControls({ attribution: true }).extend([attribution])

      const map = new OLMap(mapOptions)

      // Store map in app object (window.appObject)
      app.map = map

      const olLayersArr = __.config.layersOL3.reverse()
      for (let i = 0; i < olLayersArr.length; i++) {
        const olLayer = olLayersArr[i]
        /*
        var layer = PoziUtils.getLayerFromOLLayer(olLayer)
        if (layer.type === 'VectorTile') {
          continue
        } */

        // console.debug('map-base add layer', olLayer.get('titleid'))

        map.addLayer(olLayer)
      }

      // Activate layers that should be visible on load
      activateLayersVisibleOnLoad()

      AddScaleLine(map)

      // Store map in model. Note: due to timing issues, currently this MUST
      // happen AFTER all layers are activated (activateLayersVisibleOnLoad)
      Model.set('app.map.olMap', map)

      // Mark map as loaded in redux
      window.store.dispatch(setMapLoaded(true))

      PoziMap().resize()

      // Encode state in URL on each map movement
      map.on('moveend', () => HistoryUpdate())

      // Add the selection marker to the map as an overlay
      PoziMap().setSelectMarker()

      app.selectMarker.on('change:position', PoziMap().onChangePosition)

      // Allow for free zooming when using drag/zoom functionality
      setDragZoomUnconstrained()

      // Hiding permanently the selector if configured. Defaults to visible.
      const selectorVisible = Model.get('config.layout.map.selector.visible', true)
      if (!selectorVisible) {
        PoziMap().removeMarker()
      }

      const multiLineStringVertexWidth = 15
      let multiLineStringVertexRadius = 0
      // Global config to show a dot between multiline vertexes.
      if (__.config.showMultiLineSegments === true) {
        multiLineStringVertexRadius = 5
      }

      let lineStringStroke = null // See templates.json to modify default.
      const overrideLineStringStroke = Model.get('config.layout.map.selection.stroke.color', null)
      if (overrideLineStringStroke) {
        lineStringStroke = overrideLineStringStroke
      }

      let lineStringFill = null // See templates.json to modify default.
      const overrideLineStringFill = Model.get('config.layout.map.selection.fill.color', null)
      if (overrideLineStringFill) {
        lineStringFill = overrideLineStringFill
      }

      // Feature overlay styles, per geometry type
      const fostyles = {
        Point: [],
        MultiPoint: [],
        LineString: [
          new OLStyleStyle({
            stroke: new OLStyleStroke({
              color: lineStringStroke,
              width: multiLineStringVertexWidth
            })
          }),
          new OLStyleStyle({
            image: new OLStyleCircle({
              crossOrigin: 'Anonymous',
              radius: multiLineStringVertexRadius,
              fill: new OLStyleFill({ color: lineStringFill })
            }),
            geometry: function (feature) {
              // return the points within the line
              const coordinates = feature.getGeometry().getCoordinates()
              return new OLGeomMultiPoint(coordinates)
            }
          })
        ],
        MultiLineString: [
          new OLStyleStyle({
            stroke: new OLStyleStroke({
              color: lineStringStroke,
              width: multiLineStringVertexWidth
            })
          }),
          new OLStyleStyle({
            image: new OLStyleCircle({
              crossOrigin: 'Anonymous',
              radius: multiLineStringVertexRadius,
              fill: new OLStyleFill({ color: lineStringFill })
            }),
            geometry: function (feature) {
              // return the points within the multiline getCoordinates is an array of
              // coordinate pairs, that we flatten using reduce
              const coordinates = feature
                .getGeometry()
                .getCoordinates()
                .reduce(function (a, b) {
                  return a.concat(b)
                })
              return new OLGeomMultiPoint(coordinates)
            }
          })
        ],
        Polygon: [
          new OLStyleStyle({
            stroke: new OLStyleStroke({ color: lineStringStroke, width: 3 }),
            fill: new OLStyleFill({
              color: lineStringFill
            })
          })
        ],
        MultiPolygon: [
          new OLStyleStyle({
            stroke: new OLStyleStroke({ color: lineStringStroke, width: 3 }),
            fill: new OLStyleFill({ color: lineStringFill })
          })
        ]
      }

      // Feature overlay styles, per geometry type
      const foStyle = function (feature, resolution) {
        const geom = feature.getGeometry()
        if (geom) {
          const geomType = geom.getType()
          return fostyles[geomType]
        }
        return null
      }

      // Feature overlay for highlight
      app.featureOverlay = new OLLayerVector({
        map,
        source: new OLSourceVector({
          features: new OLCollection(),
          useSpatialIndex: false // optional, might improve performance
        }),
        style: foStyle,
        updateWhileAnimating: true, // optional, for instant visual feedback
        updateWhileInteracting: true // optional, for instant visual feedback
      })

      app.setVectorFeaturesDefault = function (olLayer, exceptFeature) {
        console.log('PoziMapBase.setVectorFeaturesDefault() PoziMap: SetVectorFeaturesDefault...')
        if (typeof olLayer !== 'object') {
          console.log('PoziMapBase.setVectorFeaturesDefault() SelectVectorFeature Invalid olLayer.')
          return
        }

        // console.log("PoziMap: setVectorFeaturesDefault");

        const title = olLayer.get('title') || null
        if (isNil(title)) {
          console.log('PoziMapBase.setVectorFeaturesDefault() Unable to find title in layer, temporary layer clicked?')
          return
        }

        const layer = get(app, 'layerConfig[' + title + '].layer', null)
        if (isNil(layer)) {
          console.log('PoziMapBase.setVectorFeaturesDefault() Unable to find layer for title', title)
          return
        }

        const source = layer.getSource()
        const layerConf = get(app, 'layerConfig[' + title + '].config', null)
        if (isNil(layerConf)) {
          console.log('PoziMapBase.setVectorFeaturesDefault() Unable to find layer config', title)
          return
        }

        const id = layerConf.config.spatial.id
        const exceptFeatureID = PoziUtils.getFeaturePropertyValueCaseInsensitive(exceptFeature, id, -1)
        const layerConfigTemp = app.layerConfig[olLayer.get('title')]
        const layerConfigStyleTemp = layerConfigTemp.style.default

        app.poziMap.removeAllOverlayFeatures()
        let featureID = null
        if (layerConfigStyleTemp) {
          source.forEachFeature(function (feat) {
            featureID = PoziUtils.getFeaturePropertyValueCaseInsensitive(feat, id)
            if (equalsIgnoreCase(featureID, exceptFeatureID)) {
              // Troubleshooting tip: Can fail to set the default style if the style config is misconfigured. This can be as simple as an invalid color.
              // console.log("PoziMap: setVectorFeaturesDefault: Set default style for feature id:", featureID)
              feat.setStyle(layerConfigStyleTemp)
            } else {
              console.log('PoziMapBase.setVectorFeaturesDefault() Leave style for id:', exceptFeatureID)
            }
          })
        }
      }

      // Setup GPS functions
      const showGPSButtons2 = Model.get('config.layout.map.buttons.gps.visible', true)
      if (showGPSButtons2 && (window.location.protocol === 'https:' || Model.get('app.localhost'))) {
        PoziMap().gpsInit()
      }

      const showOpenlayersAttribution = Model.get('config.layout.map.attribution.openlayers.visible', true)
      if (!showOpenlayersAttribution) {
        $('#map .ol-attribution').hide()
      }

      // Dismissable message box
      if (__.config.message) {
        $('#message_box').append(
          $('<div>')
            .attr('class', 'alert alert-' + (__.config.message.type || 'warning') + ' alert-dismissible')
            .css({
              'margin-left': '20px'
            })
            .attr('role', 'alert')
            .append(
              $('<button>')
                .attr('type', 'button')
                .attr('class', 'close')
                .attr('data-dismiss', 'alert')
                .append(
                  $('<span>')
                    .attr('aria-hidden', 'true')
                    .html('&times;')
                )
                .append(
                  $('<span>')
                    .attr('class', 'sr-only')
                    .html('Close')
                )
            )
            .append(__.config.message.content)
        )
      }

      // Feature overlay for routing
      app.routingOverlay = new OLLayerVector({
        map,
        source: new OLSourceVector({
          features: new OLCollection(),
          useSpatialIndex: false // optional, might improve performance
        }),
        style: [
          new OLStyleStyle({
            stroke: new OLStyleStroke({
              color: 'rgba(0, 90, 255, 0.4)',
              width: 10
            })
          }),
          new OLStyleStyle({
            stroke: new OLStyleStroke({
              color: 'rgba(0, 90, 255, 0.4)',
              width: 8
            })
          })
        ],
        updateWhileAnimating: true, // optional, for instant visual feedback
        updateWhileInteracting: true // optional, for instant visual feedback
      })

      app.routingError = (errorMsg, errorCode, routingOpts) => {
        console.error('Routing error', errorMsg, errorCode, routingOpts = {})

        // Show error in routing info div
        $('#routinginfo').html(errorMsg || '')

        // Show snackbar
        PoziNotifications().warning(errorMsg)

        // Report error in Google Analytics
        PoziUtils.gtag({
          category: 'useraction',
          status: 'warning',
          identifier: 'Routing ' + routingOpts.by,
          action: 'Routing search failed',
          additionalInfo: errorMsg
        })
      }

      // Performing and showing routing
      app.showRoute = (routingOpts, moreOpts) => {
        console.log('showRoute', routingOpts, moreOpts)
        app.hideRoute('Loading route ...', routingOpts, moreOpts)

        if (
          Object.prototype.hasOwnProperty.call(routingOpts, 'from') &&
          Object.prototype.hasOwnProperty.call(routingOpts, 'to') &&
          Object.prototype.hasOwnProperty.call(routingOpts, 'by')
        ) {
          // JSON call to MapQuest routing API
          const ajaxOptions = {
            url: 'https://www.mapquestapi.com/directions/v2/route',
            data: $.extend(moreOpts || {}, {
              from: routingOpts.from[1] + ',' + routingOpts.from[0],
              to: routingOpts.to[1] + ',' + routingOpts.to[0],
              shapeFormat: 'raw',
              routeType: routingOpts.by,
              generalize: 0,
              key: decodeURIComponent('ctYRbAHQJSRi8t26ltuBMgWwbYXHW8uJ'),
              walkingSpeed: 5,
              unit: 'k'
            }),
            success: (json) => {
              console.info('route', json)
              const statusCode = get(json, 'info.statuscode', undefined)
              if (statusCode !== 0) {
                // No routing can be displayed
                const infoMsg = get(json, 'info.messages', []).join(' ')
                app.routingError(infoMsg, statusCode, routingOpts)
              } else {
                // Display time and distance
                $('#routinginfo').html(Math.round(json.route.time / 60) + ' min (' + Math.round(json.route.distance * 10) / 10 + ' km)')

                // Returned route coordinates are in shapePoints and need re-processing
                const coordArr = []
                const jsonPtsArr = json.route.shape.shapePoints

                // Looping to get the coordinates in the right order and projection
                for (let k = 0; k < jsonPtsArr.length; k = k + 2) {
                  coordArr.push(app.transformFrom4326([jsonPtsArr[k + 1], jsonPtsArr[k]]))
                }

                // Build the geometry
                const routeGeom = new OLGeomLineString(coordArr)

                // Build a feature with this geometry
                const routeFeature = new OLFeature({
                  geometry: routeGeom,
                  name: 'My Route'
                })
                app.routingOverlay.getSource().addFeature(routeFeature)

                try {
                  window.store.dispatch(setDirectionsTravelDistanceKM(json.route.distance))
                } catch (e) {
                  console.log(e)
                }

                PoziUtils.gtag({
                  category: 'useraction',
                  status: 'success',
                  identifier: 'Routing ' + routingOpts.by,
                  action: 'Routing search'
                })
              }
            },
            error: (error = {}) => {
              const errMsg = error.message || 'Route service unavailable'
              app.routingError(errMsg, error.code, routingOpts)
            }
          }

          // Make the actual call
          PoziAPIData().ajax(ajaxOptions)
        } else {
          const errMsg = 'Route search failed'
          app.routingError(errMsg, undefined, routingOpts)
        }
      }

      app.hideRoute = function (infoMsg) {
        console.log('Hide route.. removing features from the routing overlay')
        app.routingOverlay.getSource().clear()
        // Clearing the routing info div
        $('#routinginfo').html(infoMsg || '')
      }

      // Enable the legend popover
      $('#legendButton')
        .popover({ html: true })
        .on('shown.bs.popover', function () {
          const that = this
          // Attach a click event to the popover
          $('#' + $(this).attr('aria-describedby')).on('click', function () {
            $(that).popover('hide')
          })
        })

      const showGPSButtons = Model.get('config.layout.map.buttons.gps.visible', true)
      if (!showGPSButtons) {
        $('#gpsButton').hide()
        $('#gpsFollowButton').hide()
      }

      // $('#editButton').css({'display': ((configEdit && urlEdit) ? 'inline' : 'none')})
      // Adding an opacity slider if there is an imagery/satellite group
      const aerialphotoControlType = Model.get('config.layout.map.buttons.aerialphoto.controltype', 'button') // or slider
      $('#elt-slider').css({
        display: aerialphotoControlType === 'slider' ? 'block' : 'none'
      })

      // Adding a aerial toggle button if there is an imagery/satellite group
      $('#elt-imagery-toggle').css({
        display: aerialphotoControlType === 'button' ? 'block' : 'none'
      })

      // Binding the input opacity to the layer opacity
      $('#elt-imagery-toggle').on('click', function () {
        const aerialLayerConf = __.getAerialLayer()
        if (aerialLayerConf) {
          const { titleid } = aerialLayerConf
          const layerSelected = PoziUtils.isLayerSelected(aerialLayerConf)

          console.debug('PoziMap - getAerialLayer', titleid, layerSelected)

          if (layerSelected) {
            // UnSelectLayer(layer)
            window.store.dispatch(setLayerUnselected(titleid))
          } else {
            // SelectLayer(layer)
            window.store.dispatch(setLayerSelected(titleid))
          }
        } else {
          console.log('PoziMap - getAerialLayer - unable to find aerial layer')

          PoziNotifications().warning('Could not find an aerial layer to toggle')
        }
      })

      // Change mouse cursor when over marker
      // Reference: https://groups.google.com/forum/#!msg/ol3-dev/i3exO31gTgM/TyxZNCNBXREJ
      map.on('pointermove', function (evt) {
        // detect feature at mouse coords
        const hit = map.forEachFeatureAtPixel(evt.pixel, function (feature, layer) {
          // console.debug('pointermove - layer', feature, layer)

          // Do not try to find features when we're hovering over a map control
          const target = evt.originalEvent.target
          if (target.closest('.ol-control')) {
            return
          }

          if (typeof layer !== 'object') {
            console.log('PoziMap: SelectVectorFeature: Invalid layer.')
            return
          }

          // Displays a mouse click pointer except if the layer is configured to preventClick
          if (layer) {
            const title = layer.get('title')
            const layerConf = app.layerConfig[title]?.config

            if (layerConf) {
              const { preventClick, queryable = true } = layerConf

              // console.debug('pointermove - layer', layerConf.titleid, preventClick, queryable)

              // Skip layer if it's not set to block all clicks and is queryable
              if (!preventClick && queryable) {
                return true
              }
            }
          }
        })

        $('#map').css('cursor', hit ? 'pointer' : '')
      })

      PoziMap().enableDefaultMapClickInteration()

      // Attaching geocode function to address form submit
      $('form').submit(function (event) {
        // GA send a form submit action
        PoziUtils.gtag({
          category: 'data',
          status: 'success',
          identifier: 'Form Submitted',
          action: 'Form Submitted'
        })
        event.preventDefault()
        return false
      })

      // Show tooltips on hover over vector features. Disable when the map moves and re-enable when
      // map has finished moving. This is to make the UX better.
      map.on('movestart', disableTooltips)
      map.on('moveend', enableTooltips)

      enableTooltips()

      Model.set('app.map.initialised', true)

      console.log('Init map done.')

      return resolve()
    })
  }

  // ============================================================
  // Private Functions
  // ============================================================

  // getAerialLayer: return either the one selected in the imagery/satellite group, or the first one in this group
  __.getAerialLayer = function () {
    let aerialLayerConf, firstEncounter

    // Get groups from the redux state and find the first visible imagery layer. If that fails, find the first layer
    const groups = reduxStore.getState().groups
    for (const group of Object.values(groups)) {
      const { id, layers } = group

      // We are only interested in layers from 'Imagery' or 'Satellite' groups
      if (id !== 'imagery' && id !== 'satellite') {
        continue
      }

      // Ignore any group with no layers
      if (!layers?.length) {
        continue
      }

      for (const titleid of layers) {
        const layerConf = PoziUtils.getLayerFromTitleID(titleid)

        // Skip catalogue type layers
        const isCatalogue = PoziUtils.isLayerTypeCatalogue(layerConf)
        if (isCatalogue) {
          continue
        }

        // Choose the first visible layer
        if (layerConf.visible || layerConf.selected) {
          aerialLayerConf = layerConf
          break
        }

        // We will use this as a last resort if we haven't found a suitable visible layer
        if (!firstEncounter) {
          firstEncounter = layerConf
        }
      }
    }

    // If no aerial layer is selected, the last encountered (i.e. the top in the group) is made visible and returned
    if (!aerialLayerConf) {
      aerialLayerConf = firstEncounter
    }

    // console.debug('getAerialLayer', aerialLayerConf?.titleid, aerialLayerConf)

    return aerialLayerConf
  }
}
